import { useCallback, useEffect } from "react";
import { listThreads, THREAD_LIST_LIMIT } from "../../../api/threads/threadsApi";
import type { ThreadSummary } from "../../../api/threads/threadDecoders";
import { useLoad, type LoadState } from "../../../shared/loading/useLoad";
import { shownThreads } from "../list/projectThreads";
import { useLiveProject } from "../live/threadLiveContext";
import { onThreadListChanged } from "./threadListEvents";

export interface ProjectThreads {
  readonly state: LoadState<readonly ThreadSummary[]>;
  readonly reload: () => void;
}

/**
 * A project's Threads, each with its status (Q-61), kept current by the project feed of the
 * live socket (Q-62). The list is read over HTTP first, and again on focus or after a start
 * or fork in this tab while the socket's list is not current (socket down or refused).
 */
export function useProjectThreads(projectId: string): ProjectThreads {
  const load = useCallback((signal: AbortSignal) => listThreads(projectId, signal), [projectId]);
  const { state, reload, refresh } = useLoad(load);
  const followed = useLiveProject(projectId, THREAD_LIST_LIMIT);
  const following = followed?.delivery === "following";

  useEffect(() => {
    if (following) {
      return undefined;
    }
    const unsubscribe = onThreadListChanged(refresh);
    window.addEventListener("focus", refresh);
    return () => {
      unsubscribe();
      window.removeEventListener("focus", refresh);
    };
  }, [following, refresh]);

  const threads = shownThreads(state.status === "loaded" ? state.value : null, followed);
  return { state: threads === null ? state : { status: "loaded", value: threads }, reload };
}
